import {
  BellIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  PhoneIcon,
} from "@heroicons/react/24/solid";
import { JSX } from "react";

export type ContactChannel = {
  email?: { email: string };
  sms?: { phone: string };
  webhook?: { url: string };
  slack?: { webhook_url: string };
  discord?: { webhook_url: string };
  apns?: boolean;
  pagerduty?: {
    integration_key: string;
    severity_mapping?: {
      critical: string;
      high: string;
      medium: string;
      low: string;
    };
    auto_resolve_incidents: boolean;
  };
  incidentio?: {
    webhook_url: string;
    bearer_token: string;
    auto_resolve_incidents: boolean;
  };
  opsgenie?: {
    api_key: string;
    priority?: string;
    responders?: Array<{
      type: string;
      id?: string;
      name?: string;
      username?: string;
    }>;
    tags?: string[];
    auto_close_alerts: boolean;
    eu_instance: boolean;
  };
  zendesk?: {
    subdomain: string;
    email: string;
    api_token: string;
    priority?: string;
    custom_fields?: Array<{
      id: number;
      value: string;
    }>;
    tags?: string[];
    auto_solve_tickets: boolean;
  };
  teams?: { webhook_url: string };
};

export type Contact = {
  id: string;
  account: string;
  name: string;
  channel: ContactChannel | "apns";
  active: boolean;
  down_alerts_only: boolean;
  event_types: string;
  error?: string;
};

export function ContactErrorBadge({ contact }: { contact: Contact }): JSX.Element {
  // console.log("ContactErrorBadge", contact)
  if (!contact.active) {
    const err = contact.error || "Unknown error";
    return (
      <span className="bg-red-500 text-white text-xs px-1 rounded-sm" title={err}>
        Inactive
      </span>
    );
  }

  return <></>;
}

export function ContactDetails({ contact }: { contact: Contact }) {
  if (contact.channel === "apns") {
    return (
      <span className="flex flex-row items-center gap-1 max-w-xl overflow-hidden">
        <DevicePhoneMobileIcon className="h-4 w-4" />
        <ContactErrorBadge contact={contact} />
        {contact.name}
      </span>
    );
  }

  if (contact.channel.email) {
    return (
      <span className="flex flex-row items-center gap-1 max-w-xl overflow-hidden">
        <EnvelopeIcon className="h-4 w-4" />
        <ContactErrorBadge contact={contact} />
        {/* {contact.channel.email.email} */}
        {contact.name}
      </span>
    );
  }

  if (contact.channel.sms) {
    return (
      <span className="flex flex-row items-center gap-1 max-w-xl overflow-hidden">
        <PhoneIcon className="h-4 w-4" />
        <ContactErrorBadge contact={contact} />
        {/* {contact.channel.sms.phone} */}
        {contact.name}
      </span>
    );
  }

  if (contact.channel.webhook) {
    return (
      <span className="flex flex-row items-center gap-1 max-w-xl overflow-hidden">
        <GlobeAltIcon className="h-4 w-4" />
        <ContactErrorBadge contact={contact} />
        {/* {contact.channel.webhook.url} */}
        {contact.name}
      </span>
    );
  }

  if (contact.channel.slack) {
    return (
      <span className="flex flex-row items-center gap-1 max-w-xl overflow-hidden">
        <GlobeAltIcon className="h-4 w-4" />
        <ContactErrorBadge contact={contact} />
        {/* {contact.channel.slack.webhook_url} */}
        {contact.name}
      </span>
    );
  }

  if (contact.channel.discord) {
    return (
      <span className="flex flex-row items-center gap-1 max-w-xl overflow-hidden">
        <GlobeAltIcon className="h-4 w-4" />
        <ContactErrorBadge contact={contact} />
        {contact.name}
      </span>
    );
  }

  if (contact.channel.teams) {
    return (
      <span className="flex flex-row items-center gap-1 max-w-xl overflow-hidden">
        <GlobeAltIcon className="h-4 w-4" />
        <ContactErrorBadge contact={contact} />
        {contact.name}
      </span>
    );
  }

  if (contact.channel.pagerduty) {
    return (
      <span className="flex flex-row items-center gap-1 max-w-xl overflow-hidden">
        <GlobeAltIcon className="h-4 w-4" />
        <ContactErrorBadge contact={contact} />
        {/* PagerDuty integration */}
        {contact.name}
      </span>
    );
  }

  if (contact.channel.incidentio) {
    return (
      <span className="flex flex-row items-center gap-1 max-w-xl overflow-hidden">
        <GlobeAltIcon className="h-4 w-4" />
        <ContactErrorBadge contact={contact} />
        {/* incident.io integration */}
        {contact.name}
      </span>
    );
  }

  if (contact.channel.opsgenie) {
    return (
      <span className="flex flex-row items-center gap-1 max-w-xl overflow-hidden">
        <BellIcon className="h-4 w-4" />
        <ContactErrorBadge contact={contact} />
        {/* Opsgenie integration */}
        {contact.name}
      </span>
    );
  }

  if (contact.channel.zendesk) {
    return (
      <span className="flex flex-row items-center gap-1 max-w-xl overflow-hidden">
        <GlobeAltIcon className="h-4 w-4" />
        <ContactErrorBadge contact={contact} />
        {/* Zendesk integration */}
        {contact.name}
      </span>
    );
  }

  return <span>unknown</span>;
}

export function contactTo(contact: Contact): string {
  if (contact.channel === "apns") {
    return contact.name;
  }

  if (contact.channel.email) {
    return contact.channel.email.email;
  }

  if (contact.channel.sms) {
    return contact.channel.sms.phone;
  }

  if (contact.channel.webhook) {
    return contact.channel.webhook.url;
  }

  if (contact.channel.slack) {
    return contact.channel.slack.webhook_url;
  }

  if (contact.channel.discord) {
    return contact.channel.discord.webhook_url;
  }

  if (contact.channel.teams) {
    return contact.channel.teams.webhook_url;
  }

  if (contact.channel.pagerduty) {
    return "PagerDuty Integration";
  }

  if (contact.channel.incidentio) {
    return "incident.io Integration";
  }

  if (contact.channel.opsgenie) {
    return "Opsgenie Integration";
  }

  if (contact.channel.zendesk) {
    return "Zendesk Integration";
  }

  return "unknown";
}
